/**
 * REQ-104: venue accessibility catalog, split "For fans" / "For artists".
 *
 * Plain data plus pure helpers (no React, no Firestore), so the map filter, the venue page and
 * Renee's map redesign can share it, and admins can add items later without touching the UI.
 *
 * Values are always 'yes' | 'no' | 'unknown'. A missing key means 'unknown'; nothing is backfilled.
 *
 * Sources, highest priority first:
 *   report  venue.accessibility.{fan|artist}[id] (report forms / Admin; written by people)
 *   notes   parseArtistAccessText(venue.adaAccessibilityArtist), read time only, never written
 *   osm     OpenStreetMap tags (phase C; nothing passes this yet)
 *   google  Google Places accessibilityOptions (phase C; venue page only, never on the Mapbox map)
 */

export const ACCESS_YES = 'yes';
export const ACCESS_NO = 'no';
export const ACCESS_UNKNOWN = 'unknown';

export const ACCESS_SOURCE_LABELS = {
  report: 'from reports',
  notes: 'from notes',
  osm: 'from OpenStreetMap',
  google: 'from Google',
};

export const FAN_ACCESS_ITEMS = [
  { id: 'stepFreeEntrance', label: 'Step-free / wheelchair entrance', sources: { osm: 'wheelchair', google: 'wheelchairAccessibleEntrance' } },
  { id: 'accessibleRestroom', label: 'Accessible restroom', sources: { osm: 'toilets:wheelchair', google: 'wheelchairAccessibleRestroom' } },
  { id: 'accessibleParking', label: 'Accessible parking', sources: { google: 'wheelchairAccessibleParking' } },
  { id: 'accessibleSeating', label: 'Accessible seating / viewing area', sources: { google: 'wheelchairAccessibleSeating' } },
  { id: 'liftOrRamp', label: 'Lift or ramps to all public areas', sources: {} },
  { id: 'quietSpace', label: 'Quiet / low-stimulation space', sources: {} },
  { id: 'genderNeutralRestroom', label: 'Gender-neutral restroom', sources: {} },
  { id: 'hearingAssist', label: 'Hearing assistance (loop / ALS)', sources: {} },
  { id: 'assistanceAnimals', label: 'Assistance animals welcome', sources: {} },
  { id: 'strobeNotice', label: 'Lighting / strobe warnings given', sources: {} },
];

export const ARTIST_ACCESS_ITEMS = [
  { id: 'stageStepFree', label: 'Stage step-free / ramp to stage', sources: {} },
  { id: 'greenRoomStepFree', label: 'Green room / dressing room step-free', sources: {} },
  { id: 'loadInStepFree', label: 'Load-in at street level or dock, no stairs', sources: {} },
  { id: 'elevator', label: 'Freight or passenger elevator to stage', sources: {} },
  { id: 'vanParking', label: 'Van / trailer parking at or near venue', sources: {} },
  { id: 'setupSupport', label: 'Staff support for load-in / setup', sources: {} },
];

export const ACCESS_ITEMS_BY_SIDE = { fan: FAN_ACCESS_ITEMS, artist: ARTIST_ACCESS_ITEMS };

/** 'yes' | 'no' | 'unknown' from anything a form, Admin or an outside source might store. */
export function normalizeAccessValue(raw) {
  if (raw === true) return ACCESS_YES;
  if (raw === false) return ACCESS_NO;
  const s = String(raw == null ? '' : raw).trim().toLowerCase();
  // OSM's wheelchair=limited is neither a clear yes nor a no.
  if (s === 'yes' || s === 'true' || s === 'y') return ACCESS_YES;
  if (s === 'no' || s === 'false' || s === 'n') return ACCESS_NO;
  return ACCESS_UNKNOWN;
}

// Phrase rules for the free-text "ADA Accessibility for Artist" field. Each item checks its
// rules in order and the first hit wins, so the specific phrases ("no stairs to stage",
// "no permanent ramp to the stage") sit before the general ones ("stairs to stage", "ramp to stage").
const STAGE = '(?:the )?stage';
const ARTIST_TEXT_RULES = {
  stageStepFree: [
    [new RegExp(`\\bno (?:stairs|steps) (?:up )?to ${STAGE}`), ACCESS_YES],
    [new RegExp(`\\b(?:no|not currently have an?|without an?) (?:permanent )?(?:accessible )?ramp (?:up )?to ${STAGE}`), ACCESS_NO],
    [new RegExp(`\\b(?:stairs|steps) (?:up )?to ${STAGE}`), ACCESS_NO],
    [/\bstage has [a-z\s]{0,20}\b(?:steps?|stairs)\b/, ACCESS_NO],
    [/\bstage (?:is not|isn't|no\b)/, ACCESS_NO],
    [new RegExp(`\\bramps? (?:up )?(?:to|onto) ${STAGE}`), ACCESS_YES],
    [/\bramp (?:provides access from|from) stage\b/, ACCESS_YES],
    [/\bramp [a-z]{0,4} ?under stage\b/, ACCESS_YES],
    [/\bstage (?:is )?(?:wheelchair accessible|step[- ]free|accessible)\b/, ACCESS_YES],
    [/\bstage yes\b/, ACCESS_YES],
    [/\bfloor stage\b/, ACCESS_YES],
    [/\b(?:no stairs throughout|fully (?:wheelchair )?accessible)\b/, ACCESS_YES],
  ],
  greenRoomStepFree: [
    [/\b(?:gr|green ?rooms?|dressing rooms?|backstage) (?:is |are )?(?:up|down)stairs\b/, ACCESS_NO],
    [/\b(?:gr|green ?room) no\b/, ACCESS_NO],
    [/\b(?:gr|green ?rooms?|dressing rooms?|backstage) (?:is |are )?not (?:wheelchair )?accessible\b/, ACCESS_NO],
    [/\b(?:gr|green ?room) yes\b/, ACCESS_YES],
    [/\b(?:gr|green ?rooms?|dressing rooms?|backstage) (?:is |are )?(?:wheelchair )?accessible\b/, ACCESS_YES],
    [/\belevator access to (?:the )?(?:green ?rooms?|dressing rooms?|backstage)\b/, ACCESS_YES],
    [/\b(?:no stairs throughout|fully (?:wheelchair )?accessible)\b/, ACCESS_YES],
  ],
  loadInStepFree: [
    [/\b(?:load(?:-| )?in|loading) [a-z\s]{0,20}\b(?:stairs|steps)\b/, ACCESS_NO],
    [/\b(?:flat|level|street[- ]level|ground[- ]level) load(?:-| )?(?:in|to)?\b/, ACCESS_YES],
    [/\bload(?:-| )?in (?:is )?(?:at )?(?:street|ground)[- ]level\b/, ACCESS_YES],
    [/\bloading dock\b/, ACCESS_YES],
  ],
  elevator: [
    [/\bno (?:elevator|lift)s?\b/, ACCESS_NO],
    [/\belevators?\b/, ACCESS_YES],
  ],
};

/**
 * Read-time guess from the free-text artist note. Returns only the items it is sure about,
 * e.g. { stageStepFree: 'yes', elevator: 'yes' }. Never writes anything.
 */
export function parseArtistAccessText(text) {
  const s = String(text || '').toLowerCase().replace(/[“”"]/g, '').replace(/\s+/g, ' ').trim();
  if (!s) return {};
  const out = {};
  Object.entries(ARTIST_TEXT_RULES).forEach(([id, rules]) => {
    const hit = rules.find(([re]) => re.test(s));
    if (hit) out[id] = hit[1];
  });
  return out;
}

// Artist-side phrases only: "a good view of the stage" is a fan note, "ramp to the stage" is an artist one.
const ARTIST_NOTE_RE = /\b(?:(?:to|onto|on|up to) (?:the )?stage|stage (?:access|ramp|stairs|steps|door|left|right)|green ?rooms?|dressing rooms?|backstage|load(?:-| )?in|loading dock|artist entrance)\b/i;

/**
 * Which side an approved accessibility cone note belongs under: notes about the stage, green room or
 * load-in go to artists; everything else (any `area`: outside, inside, both) is for fans.
 */
export function accessNoteSide(note) {
  return ARTIST_NOTE_RE.test(String(note?.notes || '')) ? 'artist' : 'fan';
}

/** Only approved accessibility cone notes (`venue_spec_flags`) are ever shown. */
export function isApprovedAccessNote(note) {
  return Boolean(note) && note.category === 'accessibility' && note.status === 'approved'
    && String(note.notes || '').trim().length > 0;
}

function sourceValue(sourceData, key) {
  if (!sourceData || !key) return ACCESS_UNKNOWN;
  return normalizeAccessValue(sourceData[key]);
}

/**
 * One value for one item, with where it came from.
 * opts: { osm, google, showGoogle, parsedNotes } (parsedNotes saves re-parsing per item).
 */
export function resolveAccessItem(venue, side, item, opts = {}) {
  const report = normalizeAccessValue(venue?.accessibility?.[side]?.[item.id]);
  if (report !== ACCESS_UNKNOWN) return { value: report, source: 'report' };
  if (side === 'artist') {
    const parsed = opts.parsedNotes || parseArtistAccessText(venue?.adaAccessibilityArtist);
    if (parsed[item.id]) return { value: parsed[item.id], source: 'notes' };
  }
  const osm = sourceValue(opts.osm, item.sources?.osm);
  if (osm !== ACCESS_UNKNOWN) return { value: osm, source: 'osm' };
  if (opts.showGoogle) {
    const google = sourceValue(opts.google, item.sources?.google);
    if (google !== ACCESS_UNKNOWN) return { value: google, source: 'google' };
  }
  return { value: ACCESS_UNKNOWN, source: null };
}

/** The value the map filter uses: 'yes' | 'no' | 'unknown' (report > notes > OSM; never Google). */
export function venueAccessValue(venue, side, id) {
  const item = (ACCESS_ITEMS_BY_SIDE[side] || []).find((it) => it.id === id);
  if (!item) return ACCESS_UNKNOWN;
  return resolveAccessItem(venue, side, item).value;
}

/**
 * Everything the Accessibility section renders.
 * opts: { osm, google, showGoogle = false, approvedNotes = [] }
 * Returns { fan: [{ id, label, value, source }], artist: [...], notes: { fan: [], artist: [] },
 *           artistText, knownCount }
 */
export function resolveVenueAccessibility(venue, opts = {}) {
  const parsedNotes = parseArtistAccessText(venue?.adaAccessibilityArtist);
  const itemOpts = { ...opts, parsedNotes };
  const build = (side) => (ACCESS_ITEMS_BY_SIDE[side] || []).map((item) => ({
    id: item.id,
    label: item.label,
    ...resolveAccessItem(venue, side, item, itemOpts),
  }));
  const fan = build('fan');
  const artist = build('artist');
  const notes = { fan: [], artist: [] };
  (Array.isArray(opts.approvedNotes) ? opts.approvedNotes : [])
    .filter(isApprovedAccessNote)
    .forEach((note) => {
      notes[accessNoteSide(note)].push({
        id: note.id || null,
        text: String(note.notes).trim(),
        areaLabel: note.areaLabel || null,
      });
    });
  const artistText = String(venue?.adaAccessibilityArtist || '').trim();
  const knownCount = [...fan, ...artist].filter((row) => row.value !== ACCESS_UNKNOWN).length;
  return { fan, artist, notes, artistText, knownCount };
}
